import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Shield, Clock, Award, Users,
  ArrowRight, CheckCircle, Star, Bus, Ticket, Sparkles, Zap, TrendingUp
} from 'lucide-react';
import { COMPANY_CONFIG } from '../config/config';

const Dashboard = () => {
  const coverImages = ['/images/A2.jpg', '/images/A3.jpg', '/images/A1.jpg'];
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % coverImages.length);
    }, 4000);

    return () => clearInterval(intervalId);
  }, [coverImages.length]);

  const services = [
    {
      title: 'Safe & Reliable',
      description: 'Well-maintained fleet with experienced professional drivers.',
      icon: Shield
    },
    {
      title: 'Punctual Service',
      description: 'On-time departures and arrivals for every journey.',
      icon: Clock
    },
    {
      title: 'Premium Quality',
      description: 'Comfortable seating and modern amenities.',
      icon: Award
    },
    {
      title: '24/7 Support',
      description: 'Round-the-clock customer assistance.',
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-50">
      {/* Hero Carousel Section */}
      <section className="relative h-[260px] sm:h-[340px] lg:h-[540px] overflow-hidden bg-[#00336a]">
        {coverImages.map((image, index) => (
          <div
            key={image}
            className={`absolute inset-0 transition-opacity duration-700 ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
            aria-label={`Nuwantha cover slide ${index + 1}`}
          />
        ))}

        <div className="absolute inset-0 bg-black/45" />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 flex flex-col items-center justify-center text-center">
          <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 lg:mb-10 text-blue-100 font-bold">
            Book Your Seat Online - Fast & Easy
          </p>

          <div className="mb-5 sm:mb-6">
            <a
              href="https://www.tripimate.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-white text-[#00336a] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg font-bold hover:bg-gray-100 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 border-2 border-blue-300 hover:border-blue-400"
            >
              <span>Book Your Seat Online Now</span>
            </a>
          </div>

          <p className="text-xs sm:text-sm text-blue-100 flex items-center justify-center space-x-1.5 sm:space-x-2">
            <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 text-blue-200" />
            <span>Instant Confirmation • Secure Payment • Easy Cancellation</span>
          </p>
        </div>

        <div className="absolute bottom-4 left-0 right-0 z-10 flex justify-center gap-2">
          {coverImages.map((_, index) => (
            <button
              key={`dot-${index}`}
              type="button"
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 w-2.5 rounded-full border border-white/50 transition-all ${
                currentSlide === index ? 'bg-white' : 'bg-white/40'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Online Booking Benefits Section */}
      <section className="py-6 sm:py-8 lg:py-10 bg-white border-b-2 border-blue-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 lg:p-5 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg sm:rounded-xl border border-blue-100">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base lg:text-lg">Quick Booking</h3>
                <p className="text-gray-600 text-xs sm:text-sm">Book in minutes</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 lg:p-5 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg sm:rounded-xl border border-blue-100">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <Ticket className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base lg:text-lg">Easy Selection</h3>
                <p className="text-gray-600 text-xs sm:text-sm">Choose your preferred seat</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 lg:p-5 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg sm:rounded-xl border border-blue-100">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-blue-700 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base lg:text-lg">Instant Confirmation</h3>
                <p className="text-gray-600 text-xs sm:text-sm">Get confirmed immediately</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Routes Section - Highlighted */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="text-center mb-6 sm:mb-8 lg:mb-10">
            <div className="inline-flex items-center space-x-1.5 sm:space-x-2 bg-blue-100 px-2.5 sm:px-3 lg:px-4 py-1 sm:py-1.5 rounded-full mb-2 sm:mb-3">
              <TrendingUp className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-blue-700" />
              <span className="text-blue-700 font-semibold text-xs sm:text-sm">Popular Routes</span>
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-900 mb-2 sm:mb-3">
              Book Your Journey Online
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto">
              Select your route and book your seat instantly
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Route 1: Colombo Meegahakiula (38) */}
            <div 
              className="relative bg-gradient-to-br from-white via-blue-50 to-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden group"
            >
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-[#00336a] text-white px-2 sm:px-2.5 lg:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-md z-20">POPULAR</div>
              <div className="relative z-10 flex flex-col md:flex-row items-stretch gap-4">
                <div className="md:w-7/12 flex flex-col">
                  <div className="flex items-start justify-between mb-3 sm:mb-4 lg:mb-5">
                    <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#00336a] rounded-lg sm:rounded-xl flex items-center justify-center shadow-md transform group-hover:scale-105 transition-transform">
                        <Bus className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                      </div>
                      <div>
                        <div className="inline-block bg-[#00336a] text-white px-2 sm:px-3 lg:px-4 py-0.5 sm:py-1 lg:py-1.5 rounded-full text-xs sm:text-sm font-bold mb-1 sm:mb-1.5 shadow-md">Route #38</div>
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-[#00336a] drop-shadow-sm">Colombo → Meegahakiula</h3>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 sm:space-x-4 lg:space-x-6 mb-3 sm:mb-4 lg:mb-5 text-gray-700">
                    <div className="flex items-center space-x-1.5 sm:space-x-2 bg-blue-100 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg">
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[#00336a]" />
                      <span className="font-bold text-xs sm:text-sm">Long Distance</span>
                    </div>
                    <div className="flex items-center space-x-1.5 sm:space-x-2 bg-blue-100 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[#00336a]" />
                      <span className="font-bold text-xs sm:text-sm">Daily Service</span>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4 sm:mb-5 lg:mb-6 leading-relaxed font-medium text-xs sm:text-sm lg:text-base">
                    Experience comfortable travel from Colombo to Meegahakiula with our reliable and safe bus service.
                  </p>
                  <a
                    href="https://www.tripimate.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative w-full bg-[#00336a] text-white px-4 sm:px-5 lg:px-6 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-center block hover:bg-blue-800 transition-all duration-300 font-bold text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-1.5 sm:space-x-2 border-2 border-blue-700 hover:border-blue-600 overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                    <span className="relative z-10">Book Seat Online</span>
                  </a>
                </div>
                <div className="md:w-5/12 flex items-center justify-center">
                  <img
                    src="/images/b1_1.jpeg"
                    alt="Colombo to Meegahakiula route"
                    className="w-40 sm:w-44 md:w-48 h-auto object-contain rounded-lg sm:rounded-xl md:rounded-2xl"
                  />
                </div>
              </div>
            </div>

            {/* Route 2: Katunayaka-Kandy Badulla */}
            <div 
              className="relative bg-gradient-to-br from-white via-blue-50 to-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden group"
            >
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-gradient-to-r from-[#00336a] to-blue-700 text-white px-2 sm:px-2.5 lg:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-md z-20">EXPRESS</div>
              <div className="relative z-10 flex flex-col md:flex-row items-stretch gap-4">
                <div className="md:w-7/12 flex flex-col">
                  <div className="flex items-start justify-between mb-3 sm:mb-4 lg:mb-5">
                    <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#00336a] rounded-lg sm:rounded-xl flex items-center justify-center shadow-md transform group-hover:scale-105 transition-transform">
                        <Bus className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                      </div>
                      <div>
                        <div className="inline-block bg-gradient-to-r from-[#00336a] to-blue-700 text-white px-2 sm:px-3 lg:px-4 py-0.5 sm:py-1 lg:py-1.5 rounded-full text-xs sm:text-sm font-bold mb-1 sm:mb-1.5 shadow-md">Express Route</div>
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-[#00336a] drop-shadow-sm">Katunayaka → Kandy → Badulla</h3>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 sm:space-x-4 lg:space-x-6 mb-3 sm:mb-4 lg:mb-5 text-gray-700">
                    <div className="flex items-center space-x-1.5 sm:space-x-2 bg-blue-100 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg">
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[#00336a]" />
                      <span className="font-bold text-xs sm:text-sm">Multi-City</span>
                    </div>
                    <div className="flex items-center space-x-1.5 sm:space-x-2 bg-blue-100 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[#00336a]" />
                      <span className="font-bold text-xs sm:text-sm">Daily Service</span>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4 sm:mb-5 lg:mb-6 leading-relaxed font-medium text-xs sm:text-sm lg:text-base">
                    Travel from Katunayaka through Kandy to Badulla with convenient stops and modern amenities.
                  </p>
                  <a
                    href="https://www.tripimate.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative w-full bg-[#00336a] text-white px-4 sm:px-5 lg:px-6 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-center block hover:bg-blue-800 transition-all duration-300 font-bold text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-1.5 sm:space-x-2 border-2 border-blue-700 hover:border-blue-600 overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                    <span className="relative z-10">Book Seat Online</span>
                  </a>
                </div>
                <div className="md:w-5/12 flex items-center justify-center">
                  <img
                    src="/images/b2_2.jpeg"
                    alt="Katunayaka to Kandy to Badulla route"
                    className="w-40 sm:w-44 md:w-48 h-auto object-contain rounded-lg sm:rounded-xl md:rounded-2xl"
                  />
                </div>
              </div>
            </div>

            {/* Route 3: Colombo-Kandy */}
            <div 
              className="relative bg-gradient-to-br from-white via-blue-50 to-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden group"
            >
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-gradient-to-r from-[#00336a] to-blue-700 text-white px-2 sm:px-2.5 lg:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-md z-20">NEW</div>
              <div className="relative z-10 flex flex-col md:flex-row items-stretch gap-4">
                <div className="md:w-7/12 flex flex-col">
                  <div className="flex items-start justify-between mb-3 sm:mb-4 lg:mb-5">
                    <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#00336a] rounded-lg sm:rounded-xl flex items-center justify-center shadow-md transform group-hover:scale-105 transition-transform">
                        <Bus className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                      </div>
                      <div>
                        <div className="inline-block bg-gradient-to-r from-[#00336a] to-blue-700 text-white px-2 sm:px-3 lg:px-4 py-0.5 sm:py-1 lg:py-1.5 rounded-full text-xs sm:text-sm font-bold mb-1 sm:mb-1.5 shadow-md">City Route</div>
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-[#00336a] drop-shadow-sm">Colombo → Kandy</h3>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 sm:space-x-4 lg:space-x-6 mb-3 sm:mb-4 lg:mb-5 text-gray-700">
                    <div className="flex items-center space-x-1.5 sm:space-x-2 bg-blue-100 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg">
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[#00336a]" />
                      <span className="font-bold text-xs sm:text-sm">City Travel</span>
                    </div>
                    <div className="flex items-center space-x-1.5 sm:space-x-2 bg-blue-100 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[#00336a]" />
                      <span className="font-bold text-xs sm:text-sm">Daily Service</span>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4 sm:mb-5 lg:mb-6 leading-relaxed font-medium text-xs sm:text-sm lg:text-base">
                    Enjoy a comfortable journey from Colombo to Kandy with convenient stops and modern amenities.
                  </p>
                  <a
                    href="https://www.tripimate.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative w-full bg-[#00336a] text-white px-4 sm:px-5 lg:px-6 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-center block hover:bg-blue-800 transition-all duration-300 font-bold text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-1.5 sm:space-x-2 border-2 border-blue-700 hover:border-blue-600 overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                    <span className="relative z-10">Book Seat Online</span>
                  </a>
                </div>
                <div className="md:w-5/12 flex items-center justify-center">
                  <img
                    src="/images/b2_2.jpeg"
                    alt="Colombo to Kandy route"
                    className="w-40 sm:w-44 md:w-48 h-auto object-contain rounded-lg sm:rounded-xl md:rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section with Light Blue Theme */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-b from-blue-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="text-center mb-6 sm:mb-8 lg:mb-10">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-900 mb-2 sm:mb-3">
              Why Choose Us
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto">
              Your comfort and safety are our top priorities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-blue-100"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-600 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 lg:mb-5 mx-auto shadow-md">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 mb-2 sm:mb-3 text-center">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-center leading-relaxed text-xs sm:text-sm">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section with Gradient */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-r from-blue-500 via-blue-700 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-700/50 to-blue-700/50"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center transform hover:scale-105 transition-transform">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-1 sm:mb-2">24+</div>
              <div className="text-sm sm:text-base lg:text-lg text-blue-100">Years of Experience</div>
            </div>
            <div className="text-center transform hover:scale-105 transition-transform">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-1 sm:mb-2">20+</div>
              <div className="text-sm sm:text-base lg:text-lg text-blue-100">Modern Buses</div>
            </div>
            <div className="text-center transform hover:scale-105 transition-transform">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-1 sm:mb-2">8+</div>
              <div className="text-sm sm:text-base lg:text-lg text-blue-100">Routes Covered</div>
            </div>
            <div className="text-center transform hover:scale-105 transition-transform">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-1 sm:mb-2">24/7</div>
              <div className="text-sm sm:text-base lg:text-lg text-blue-100">Customer Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section with Booking Button */}
      <section className="py-8 sm:py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8 text-center border-2 border-blue-200 shadow-lg">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-900 mb-3 sm:mb-4">
              Ready to Book Your Journey?
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-5 sm:mb-6 lg:mb-8 max-w-2xl mx-auto">
              Book your seat online now and enjoy a comfortable journey across Sri Lanka
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-5 sm:mb-6 lg:mb-8">
              <a
                href="https://www.tripimate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="relative bg-gradient-to-r from-blue-600 via-blue-300 to-blue-500 text-white px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg font-bold hover:from-blue-500 hover:via-blue-600 hover:to-blue-600 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 inline-flex items-center justify-center space-x-1.5 sm:space-x-2 border-2 border-blue-300 hover:border-blue-200 overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                <span className="relative z-10">Book Online Now</span>
              </a>
              <Link
                to="/contact"
                className="border-2 border-blue-700 text-blue-700 px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg font-bold hover:bg-blue-700 hover:text-white transition-all duration-200 inline-flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 lg:gap-6 text-gray-600">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Phone className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-blue-700" />
                <span className="font-medium text-xs sm:text-sm lg:text-base">{COMPANY_CONFIG.phone}</span>
              </div>
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Mail className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-blue-700" />
                <span className="font-medium text-xs sm:text-sm lg:text-base">{COMPANY_CONFIG.email}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
